import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, Phone, Mail, Calendar, AlertTriangle, MessageCircle, Percent, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import VendorRegistrationDialog from './VendorRegistrationDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_VENDOR = { name: '', type: 'hotel', contact: '', email: '', phone: '', gstNumber: '', bankDetails: '', notes: '' };
const VENDOR_TYPES = ['hotel', 'flight', 'transport', 'guide', 'activity', 'visa', 'dmc', 'consolidator', 'tour-operator', 'other'];

const REVENUE_METHODS = [
  { value: 'net_rate_markup', label: 'Net Rate + Markup' },
  { value: 'commission', label: 'Commission' },
  { value: 'fixed_commission', label: 'Fixed Commission' },
  { value: 'commission_markup', label: 'Commission + Markup' },
  { value: 'fixed_fee', label: 'Fixed Fee' },
  { value: 'no_revenue', label: 'No Revenue' },
  { value: 'custom', label: 'Custom' },
];
const REVENUE_METHOD_LABEL = Object.fromEntries(REVENUE_METHODS.map(m => [m.value, m.label]));
const EMPTY_TERMS = { revenueMethod: 'net_rate_markup', defaultCommissionPct: '0', defaultFixedAmount: '0', defaultMarkupType: 'percent', defaultMarkupValue: '0' };

const VERIFICATION_LABEL = { draft: 'Draft', pending_verification: 'Pending Verification', verified: 'Verified', needs_update: 'Needs Update' };
const VERIFICATION_PILL = {
  draft: 'bg-slate-100 text-slate-500',
  pending_verification: 'bg-amber-50 text-amber-700',
  verified: 'bg-emerald-50 text-emerald-700',
  needs_update: 'bg-rose-50 text-rose-700',
};

const fmtINR = (n) => `₹${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;
const fmtDate = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return null;
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const AccountingVendors = ({ vendors, expenses = [], onRefresh }) => {
  const { user } = useAuth();
  const canEditTerms = user?.role === 'admin' || user?.role === 'accounts';
  const canEditSensitive = user?.role === 'admin' || user?.role === 'accounts';
  const isAgent = user?.role === 'agent';
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_VENDOR);
  const [saving, setSaving] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [termsVendorId, setTermsVendorId] = useState(null);
  const [termsForm, setTermsForm] = useState(EMPTY_TERMS);
  const [savingTerms, setSavingTerms] = useState(false);
  const [showRegDialog, setShowRegDialog] = useState(false);
  const [regVendorId, setRegVendorId] = useState(null);
  const regVendor = vendors.find((v) => v.vendorId === regVendorId) || null;

  // Aggregate per-vendor balances from expenses. For each vendor:
  //   - billed = sum of totalAmount
  //   - paid   = sum of paidAmount (or totalAmount if paymentStatus === 'paid')
  //   - balance = billed - paid
  //   - nextDue = earliest dueDate among unpaid/partial expenses (future-only)
  const balanceByVendor = useMemo(() => {
    const map = {};
    const now = Date.now();
    for (const e of expenses) {
      const vid = e.vendorId;
      if (!vid) continue;
      if (!map[vid]) map[vid] = { billed: 0, paid: 0, nextDue: null, overdue: false };
      const total = Number(e.totalAmount || 0);
      const paid = Number(e.paidAmount || (e.paymentStatus === 'paid' ? total : 0));
      map[vid].billed += total;
      map[vid].paid += paid;
      if (paid < total && e.dueDate) {
        const d = new Date(e.dueDate).getTime();
        if (!isNaN(d)) {
          if (!map[vid].nextDue || d < new Date(map[vid].nextDue).getTime()) {
            map[vid].nextDue = e.dueDate;
          }
          if (d < now) map[vid].overdue = true;
        }
      }
    }
    Object.values(map).forEach((m) => { m.balance = m.billed - m.paid; });
    return map;
  }, [expenses]);

  const openNew = () => { setForm(EMPTY_VENDOR); setEditing(null); setShowDialog(true); };
  const openEdit = (v) => {
    setForm({ name: v.name, type: v.type || 'other', contact: v.contact || '', email: v.email || '', phone: v.phone || '', gstNumber: v.gstNumber || '', bankDetails: v.bankDetails || '', notes: v.notes || '' });
    setEditing(v.vendorId);
    setShowDialog(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/accounting/vendors/${editing}`, form);
        toast.success('Vendor updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/accounting/vendors`, form);
        toast.success('Vendor created');
      }
      setShowDialog(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save vendor');
    } finally { setSaving(false); }
  };

  const handleDelete = async (vendorId) => {
    if (!window.confirm('Delete this vendor?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/accounting/vendors/${vendorId}`);
      toast.success('Vendor deleted');
      onRefresh();
    } catch (err) { toast.error('Failed to delete vendor'); }
  };

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const openTerms = (v) => {
    setTermsVendorId(v.vendorId);
    setTermsForm({
      revenueMethod: v.revenueMethod || 'net_rate_markup',
      defaultCommissionPct: String(v.defaultCommissionPct ?? 0),
      defaultFixedAmount: String(v.defaultFixedAmount ?? 0),
      defaultMarkupType: v.defaultMarkupType || 'percent',
      defaultMarkupValue: String(v.defaultMarkupValue ?? 0),
    });
    setShowTermsDialog(true);
  };

  const setTerms = (key, val) => setTermsForm(prev => ({ ...prev, [key]: val }));

  const openRegistration = (v) => { setRegVendorId(v.vendorId); setShowRegDialog(true); };

  const handleSaveTerms = async (e) => {
    e.preventDefault();
    setSavingTerms(true);
    try {
      await axios.put(`${BACKEND_URL}/api/accounting/vendors/${termsVendorId}/commercial-terms`, {
        revenueMethod: termsForm.revenueMethod,
        defaultCommissionPct: parseFloat(termsForm.defaultCommissionPct) || 0,
        defaultFixedAmount: parseFloat(termsForm.defaultFixedAmount) || 0,
        defaultMarkupType: termsForm.defaultMarkupType,
        defaultMarkupValue: parseFloat(termsForm.defaultMarkupValue) || 0,
      });
      toast.success('Commercial terms saved');
      setShowTermsDialog(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save commercial terms');
    } finally { setSavingTerms(false); }
  };

  // ----------------------------------------------------------------------
  // WhatsApp reminder — opens wa.me/<phone>?text=<prefilled msg> in a new
  // tab. Pulls balance + nextDue from the local aggregate; falls back to
  // sensible placeholders when the vendor has no expenses yet. The trip
  // ID / destination intentionally use placeholders because a vendor may
  // span multiple trips — agent fills the specifics post-paste if needed.
  // ----------------------------------------------------------------------
  const sendWhatsAppReminder = (vendor) => {
    if (!vendor.phone) {
      toast.error(`No phone on file for ${vendor.name}`);
      return;
    }
    const bal = balanceByVendor[vendor.vendorId];
    const amount = bal && bal.balance > 0 ? `₹${Math.round(bal.balance).toLocaleString('en-IN')}` : '₹[amount]';
    const dueDate = bal && bal.nextDue ? fmtDate(bal.nextDue) : '[due_date]';
    // Find any unsettled expense to reference its trip context.
    const sampleExp = expenses.find((e) => (
      e.vendorId === vendor.vendorId
      && Number(e.totalAmount || 0) > Number(e.paidAmount || 0)
    ));
    const tripId = sampleExp?.tripId || '[trip_id]';
    const destination = sampleExp?.description || '[trip_destination]';
    const agentName = 'Travcations Team';
    const msg = [
      `Hi ${vendor.name},`,
      '',
      `Quick reminder — balance of ${amount} is due on ${dueDate}`,
      `for ${destination} (Trip: ${tripId}).`,
      '',
      'Please confirm receipt or flag if any issues.',
      '',
      'Thanks,',
      `${agentName} — Travcations`,
    ].join('\n');
    // Strip non-digits and the leading + so wa.me accepts the number.
    const cleanPhone = String(vendor.phone).replace(/[^0-9]/g, '');
    const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <div className="space-y-4" data-testid="vendors-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{vendors.length} vendor{vendors.length !== 1 ? 's' : ''} registered</p>
        <Button onClick={openNew} data-testid="add-vendor-btn" className="bg-teal-700 hover:bg-teal-800 text-white">
          <Plus className="h-4 w-4 mr-2" />Add Vendor
        </Button>
      </div>

      {vendors.length === 0 ? (
        <Card className="p-12 text-center">
          <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No vendors yet. Add your first vendor to start tracking expenses.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vendors.map(v => {
            const bal = balanceByVendor[v.vendorId];
            const hasBalance = bal && (bal.billed > 0 || bal.balance > 0);
            return (
            <Card key={v.vendorId} className="p-5 hover:shadow-md transition-shadow" data-testid={`vendor-card-${v.vendorId}`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-slate-900">{v.name}</h3>
                  <span className="inline-block px-2 py-0.5 mt-1 rounded-full text-[10px] font-semibold uppercase bg-teal-100 text-teal-700">
                    {v.type || 'other'}
                  </span>
                </div>
                <div className="flex gap-1">
                  {v.phone && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => sendWhatsAppReminder(v)}
                      data-testid={`whatsapp-vendor-${v.vendorId}`}
                      title="Send WhatsApp payment reminder"
                    >
                      <MessageCircle className="h-3.5 w-3.5 text-emerald-500" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openRegistration(v)}
                    data-testid={`registration-btn-${v.vendorId}`}
                    title="Registration, documents & verification"
                  >
                    <ShieldCheck className="h-3.5 w-3.5 text-teal-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openTerms(v)}
                    data-testid={`commercial-terms-btn-${v.vendorId}`}
                    title={canEditTerms ? 'Edit commercial terms' : 'View commercial terms'}
                  >
                    <Percent className="h-3.5 w-3.5 text-indigo-400" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(v)} data-testid={`edit-vendor-${v.vendorId}`}>
                    <Pencil className="h-3.5 w-3.5 text-slate-400" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(v.vendorId)} data-testid={`delete-vendor-${v.vendorId}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                  </Button>
                </div>
              </div>
              {v.gstNumber && <p className="text-xs text-slate-500 mb-1">GST: {v.gstNumber}</p>}
              <div className="flex flex-col gap-1 mt-2 text-xs text-slate-500">
                {v.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{v.phone}</span>}
                {v.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{v.email}</span>}
              </div>
              <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                {v.commercialTermsConfigured ? (
                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700" data-testid={`commercial-terms-badge-${v.vendorId}`}>
                    {REVENUE_METHOD_LABEL[v.revenueMethod] || v.revenueMethod}
                  </span>
                ) : (
                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700" data-testid={`commercial-terms-badge-${v.vendorId}`}>
                    Commercial terms not configured
                  </span>
                )}
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${VERIFICATION_PILL[v.verificationStatus] || VERIFICATION_PILL.draft}`} data-testid={`verification-badge-${v.vendorId}`}>
                  {VERIFICATION_LABEL[v.verificationStatus] || 'Draft'}
                </span>
              </div>
              {/* Ledger summary — only render when there's an expense recorded. */}
              {hasBalance && (
                <div
                  data-testid={`vendor-balance-${v.vendorId}`}
                  className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-2 text-[11px]"
                >
                  <div>
                    <div className="uppercase tracking-wide text-slate-400">Billed</div>
                    <div className="font-semibold text-slate-700">{fmtINR(bal.billed)}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wide text-slate-400">Paid</div>
                    <div className="font-semibold text-emerald-700">{fmtINR(bal.paid)}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wide text-slate-400">Balance</div>
                    <div className={`font-semibold ${bal.balance > 0 ? 'text-amber-700' : 'text-slate-700'}`}>
                      {fmtINR(bal.balance)}
                    </div>
                  </div>
                  {bal.nextDue && (
                    <div className={`col-span-3 mt-1 flex items-center gap-1 ${bal.overdue ? 'text-rose-600' : 'text-slate-500'}`}>
                      {bal.overdue
                        ? <AlertTriangle className="h-3 w-3" />
                        : <Calendar className="h-3 w-3" />}
                      <span>{bal.overdue ? 'Overdue' : 'Next due'}: {fmtDate(bal.nextDue)}</span>
                    </div>
                  )}
                </div>
              )}
            </Card>
          );})}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Vendor' : 'Add New Vendor'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4" data-testid="vendor-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Vendor Name *</Label>
                <Input value={form.name} onChange={e => set('name', e.target.value)} required className="mt-1" data-testid="vendor-name-input" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={v => set('type', v)}>
                  <SelectTrigger className="mt-1" data-testid="vendor-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VENDOR_TYPES.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>GST Number</Label>
                <Input value={form.gstNumber} onChange={e => set('gstNumber', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={e => set('phone', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} className="mt-1" />
              </div>
              <div className="col-span-2">
                <Label>Bank Details</Label>
                <Input value={form.bankDetails} onChange={e => set('bankDetails', e.target.value)} className="mt-1" placeholder="Account #, IFSC, Bank Name" />
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Input value={form.notes} onChange={e => set('notes', e.target.value)} className="mt-1" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="save-vendor-btn" className="bg-teal-700 hover:bg-teal-800 text-white">
                {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{canEditTerms ? 'Vendor Commercial Terms' : 'Vendor Commercial Terms (view only)'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTerms} className="space-y-4" data-testid="commercial-terms-form">
            <div>
              <Label>Default Revenue Method</Label>
              <Select value={termsForm.revenueMethod} onValueChange={v => setTerms('revenueMethod', v)} disabled={!canEditTerms}>
                <SelectTrigger className="mt-1" data-testid="revenue-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REVENUE_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Default Commission %</Label>
                <Input type="number" step="0.01" value={termsForm.defaultCommissionPct} disabled={!canEditTerms}
                  onChange={e => setTerms('defaultCommissionPct', e.target.value)} className="mt-1" data-testid="default-commission-pct-input" />
              </div>
              <div>
                <Label>Default Fixed Amount (₹)</Label>
                <Input type="number" step="0.01" value={termsForm.defaultFixedAmount} disabled={!canEditTerms}
                  onChange={e => setTerms('defaultFixedAmount', e.target.value)} className="mt-1" data-testid="default-fixed-amount-input" />
              </div>
              <div>
                <Label>Default Markup Type</Label>
                <Select value={termsForm.defaultMarkupType} onValueChange={v => setTerms('defaultMarkupType', v)} disabled={!canEditTerms}>
                  <SelectTrigger className="mt-1" data-testid="default-markup-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percent</SelectItem>
                    <SelectItem value="fixed">Fixed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Default Markup Value</Label>
                <Input type="number" step="0.01" value={termsForm.defaultMarkupValue} disabled={!canEditTerms}
                  onChange={e => setTerms('defaultMarkupValue', e.target.value)} className="mt-1" data-testid="default-markup-value-input" />
              </div>
            </div>
            {!canEditTerms && (
              <p className="text-xs text-slate-500">Only Admin and Accounts can edit commercial terms.</p>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowTermsDialog(false)}>Close</Button>
              {canEditTerms && (
                <Button type="submit" disabled={savingTerms} data-testid="save-commercial-terms-btn" className="bg-teal-700 hover:bg-teal-800 text-white">
                  {savingTerms ? 'Saving...' : 'Save'}
                </Button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <VendorRegistrationDialog
        vendor={regVendor}
        open={showRegDialog}
        onClose={() => setShowRegDialog(false)}
        onRefresh={onRefresh}
        canEditSensitive={canEditSensitive}
        isAgent={isAgent}
      />
    </div>
  );
};

export default AccountingVendors;
